import React from 'react';
import { Flag, Chip, Button, Icon } from './primitives.jsx';

// ============================================================================
// Screen: Bracket Knockout Tree (Horizontal layout, 5 rounds)
// ============================================================================

const BracketMatch = ({ match, pool, onEditMatch, isAdmin }) => {
  const getTeam = (id) => pool.teams.find(t => t.id === id);
  const tA = getTeam(match.teamA_id);
  const tB = getTeam(match.teamB_id);

  const winner = match.winner_id;
  const isCompleted = match.status === 'completed';

  const isHighlighted = false;

  return (
    <div
      onClick={() => isAdmin && onEditMatch(match)}
      style={{
        background: 'var(--paper)',
        border: `1.5px solid ${isHighlighted ? 'var(--blue)' : 'var(--hairline-strong)'}`,
        borderRadius: 8,
        padding: '12px 14px',
        display: 'flex', flexDirection: 'column', gap: 8,
        minWidth: 220,
        boxShadow: isHighlighted ? '0 0 0 4px rgba(47,79,252,0.10)' : 'none',
        cursor: isAdmin ? 'pointer' : 'default',
        transition: 'all 120ms'
      }}
      onMouseEnter={(e) => {
        if (isAdmin) e.currentTarget.style.borderColor = 'var(--blue)';
      }}
      onMouseLeave={(e) => {
        if (isAdmin) e.currentTarget.style.borderColor = 'var(--hairline-strong)';
      }}
    >
      <BracketRow team={tA} score={match.scoreA} winner={isCompleted && winner === tA?.id} loser={isCompleted && winner === tB?.id} />
      <div style={{ height: 1, background: 'var(--hairline)' }} />
      <BracketRow team={tB} score={match.scoreB} winner={isCompleted && winner === tB?.id} loser={isCompleted && winner === tA?.id} />
    </div>
  );
};

const BracketRow = ({ team, score, winner, loser }) => {
  if (!team) return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 22 }}>
      <span style={{ fontFamily: 'var(--font-display)', color: 'var(--ink-mute)', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em' }}>AWAITING</span>
      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-mute)' }}>—</span>
    </div>
  );
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      color: loser ? 'var(--ink-mute)' : 'var(--ink)',
      height: 22
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Flag teamId={team.id} size={24} radius={3} style={{ opacity: loser ? 0.45 : 1 }} />
        <span style={{
          fontFamily: 'var(--font-display)', fontSize: 15,
          textTransform: 'uppercase', letterSpacing: '0.04em',
          textDecoration: loser ? 'line-through' : 'none',
          color: winner ? 'var(--blue)' : (loser ? 'var(--ink-mute)' : 'var(--ink)'),
        }}>{team.country}</span>
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15, color: winner ? 'var(--blue)' : 'var(--ink)' }}>{score ?? '—'}</span>
    </div>
  );
};

export const Bracket = ({ pool, onBack, onEditMatch, isAdmin }) => {
  const bracket = pool.bracket || { r32: [], r16: [], qf: [], sf: [], f: [] };

  return (
    <section style={{ padding: '120px 32px 140px', maxWidth: 1440, margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        flexWrap: 'wrap', gap: 24, marginBottom: 40,
      }}>
        <div>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 14, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: 'var(--blue)',
          }}>POOL · {pool.id.toUpperCase()} · KNOCKOUT</span>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(48px, 6vw, 84px)',
            letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 0.95,
            margin: '12px 0 0',
          }}>BRACKET</h1>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Chip tone="blue">STAGE · PLAYOFFS</Chip>
          <Button variant="secondary" onClick={onBack} style={{ whiteSpace: 'nowrap' }}>
            <Icon name="arrow-left" size={14} />DASHBOARD
          </Button>
        </div>
      </div>

      <div style={{ overflowX: 'auto', paddingBottom: 24 }}>
        <div style={{ display: 'flex', gap: 40, minWidth: 1400, alignItems: 'stretch' }}>
          <BracketColumn label="ROUND OF 32" pad={0}>
            {bracket.r32.map((m) => (
              <BracketMatch key={m.id} match={m} pool={pool} onEditMatch={onEditMatch} isAdmin={isAdmin} />
            ))}
          </BracketColumn>

          <BracketConnectors count={16} pad={0} />

          <BracketColumn label="ROUND OF 16" pad={1}>
            {bracket.r16.map((m) => (
              <BracketMatch key={m.id} match={m} pool={pool} onEditMatch={onEditMatch} isAdmin={isAdmin} />
            ))}
          </BracketColumn>

          <BracketConnectors count={8} pad={1} />

          <BracketColumn label="QUARTER-FINALS" pad={2}>
            {bracket.qf.map((m) => (
              <BracketMatch key={m.id} match={m} pool={pool} onEditMatch={onEditMatch} isAdmin={isAdmin} />
            ))}
          </BracketColumn>

          <BracketConnectors count={4} pad={2} />

          <BracketColumn label="SEMI-FINALS" pad={3}>
            {bracket.sf.map((m) => (
              <BracketMatch key={m.id} match={m} pool={pool} onEditMatch={onEditMatch} isAdmin={isAdmin} />
            ))}
          </BracketColumn>

          <BracketConnectors count={2} pad={3} />

          <BracketColumn label="FINAL" pad={4}>
            <div style={{ position: 'relative' }}>
              <img src="/assets/trophy.svg" alt="" style={{
                position: 'absolute', top: -56, right: -8, width: 48, opacity: 0.9,
              }} />
              {bracket.f.map((m) => (
                <BracketMatch key={m.id} match={m} pool={pool} onEditMatch={onEditMatch} isAdmin={isAdmin} />
              ))}
            </div>
          </BracketColumn>
        </div>
      </div>
    </section>
  );
};

const BracketColumn = ({ label, pad = 0, children }) => {
  const gaps = [16, 72, 184, 408, 856];
  const paddingTops = [0, 28, 84, 196, 420];

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 16,
      minWidth: 220,
    }}>
      <span style={{
        fontFamily: 'var(--font-display)', fontSize: 13,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        color: 'var(--blue)', marginBottom: 8,
      }}>{label}</span>
      <div style={{
        display: 'flex', flexDirection: 'column',
        gap: gaps[pad],
        paddingTop: paddingTops[pad],
      }}>
        {children}
      </div>
    </div>
  );
};

const BracketConnectors = ({ count, pad = 0 }) => {
  const pairs = count / 2;
  const spans = [88, 200, 424, 872];
  const paddingTops = [40, 68, 124, 236];

  const span = spans[pad];
  const paddingTop = paddingTops[pad];

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: [28, 56, 120, 240][pad],
      justifyContent: 'flex-start', paddingTop: paddingTop,
    }}>
      {Array.from({ length: pairs }).map((_, i) => (
        <svg key={i} width="32" height={span} viewBox={`0 0 32 ${span}`} style={{ display: 'block' }}>
          <path d={`M 0 ${span * 0.18} L 16 ${span * 0.18} L 16 ${span * 0.82} L 32 ${span * 0.82}`} stroke="var(--blue)" strokeWidth="1.5" fill="none" opacity="0.6" />
          <path d={`M 0 ${span * 0.82} L 16 ${span * 0.82}`} stroke="var(--blue)" strokeWidth="1.5" fill="none" opacity="0.6" />
        </svg>
      ))}
    </div>
  );
};
